import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { ArrowLeft } from 'lucide-react-native';
import { useEffect } from 'react';
import { Image, Platform, Pressable, ScrollView, StyleSheet, View } from 'react-native';
import Markdown from 'react-native-markdown-display';
import { PremiumFooter } from '../../src/components/landing/PremiumFooter';
import { Container } from '../../src/components/primitives/Container';
import { Text } from '../../src/components/primitives/Text';
import { useTranslation } from '../../src/contexts/TranslationContext';
import { trackEvent } from '../../src/lib/analytics';
import type { BlogLocale, BlogPost } from '../../src/lib/api';
import { blogAPI } from '../../src/lib/api';
import { useMixpanel } from '../../src/lib/mixpanel';
import { colors } from '../../src/theme/colors';
import { spacing } from '../../src/theme/spacing';

// ---------------------------------------------------------------------------
// Public route: /blog/:slug
// Renders a single localized blog post with a markdown body.
// ---------------------------------------------------------------------------

function CategoryBadge({ label }: { label: string }) {
  return (
    <View style={styles.tag}>
      <Text variant="caption" color={colors.primary[500]} style={styles.tagText}>
        {label}
      </Text>
    </View>
  );
}

const markdownStyles = StyleSheet.create({
  body: {
    color: colors.text.primary,
    fontSize: Platform.select({ web: 17, default: 15 }),
    lineHeight: Platform.select({ web: 28, default: 24 }),
  },
  heading1: {
    color: colors.text.primary,
    fontSize: Platform.select({ web: 28, default: 24 }),
    fontWeight: '700',
    marginTop: spacing.xl,
    marginBottom: spacing.sm,
  },
  heading2: {
    color: colors.text.primary,
    fontSize: Platform.select({ web: 22, default: 20 }),
    fontWeight: '700',
    marginTop: spacing.lg,
    marginBottom: spacing.sm,
  },
  heading3: {
    color: colors.text.primary,
    fontSize: Platform.select({ web: 18, default: 17 }),
    fontWeight: '600',
    marginTop: spacing.md,
    marginBottom: spacing.xs,
  },
  paragraph: {
    color: colors.text.secondary,
    marginBottom: spacing.md,
  },
  code_inline: {
    backgroundColor: colors.background.secondary,
    color: colors.primary[600],
    paddingHorizontal: 4,
    borderRadius: 3,
  },
  code_block: {
    backgroundColor: colors.background.secondary,
    padding: spacing.md,
    borderRadius: 6,
    marginBottom: spacing.md,
  },
  fence: {
    backgroundColor: colors.background.secondary,
    padding: spacing.md,
    borderRadius: 6,
    marginBottom: spacing.md,
  },
  bullet_list: {
    marginBottom: spacing.md,
  },
  ordered_list: {
    marginBottom: spacing.md,
  },
  link: {
    color: colors.primary[500],
  },
  blockquote: {
    borderLeftWidth: 3,
    borderLeftColor: colors.primary[300],
    paddingLeft: spacing.md,
    marginBottom: spacing.md,
  },
  hr: {
    backgroundColor: colors.border.muted,
    height: 1,
    marginVertical: spacing.lg,
  },
});

export default function BlogDetailPage() {
  const { slug } = useLocalSearchParams<{ slug: string }>();
  const router = useRouter();
  const { t, language } = useTranslation();
  const mp = useMixpanel();
  const locale = language as BlogLocale;

  const {
    data: post,
    isLoading,
    isError,
  } = useQuery<BlogPost>({
    queryKey: ['blog-post', slug, locale],
    queryFn: async () => {
      const response = await blogAPI.getPost(slug, locale);
      return response.data;
    },
    enabled: !!slug,
    // staleTime 0 — same reason as the feed: an edit to a published post has to
    // show on the next visit, not up to ten minutes later.
    staleTime: 0,
  });

  useEffect(() => {
    if (post && !isLoading) {
      trackEvent(mp, 'blog.post_viewed', {
        slug: post.slug,
        locale,
        category_count: post.categories.length,
      });
    }
  }, [post?.slug]);

  const dateLabel = post?.publishedAt ? format(new Date(post.publishedAt), 'MMMM d, yyyy') : null;

  return (
    <ScrollView style={styles.container} contentContainerStyle={styles.content}>
      <Container maxWidth="md">
        <Pressable
          testID="blog-detail-back"
          onPress={() => router.push('/blog')}
          style={styles.backButton}
          accessibilityRole="button"
          accessibilityLabel={t('blog.backToBlog')}
        >
          <ArrowLeft size={20} color={colors.primary[500]} />
          <Text variant="bodySmall" color={colors.primary[500]}>
            {t('blog.backToBlog')}
          </Text>
        </Pressable>

        {isLoading ? (
          <View testID="blog-detail-loading" style={styles.stateContainer}>
            <Text variant="body" color={colors.text.secondary}>
              {t('blog.loading')}
            </Text>
          </View>
        ) : isError ? (
          <View testID="blog-detail-error" style={styles.stateContainer}>
            <Text variant="body" color={colors.status.error.main}>
              {t('blog.error')}
            </Text>
          </View>
        ) : post ? (
          <>
            {post.coverImageUrl ? (
              <Image
                testID="blog-detail-cover"
                source={{ uri: post.coverImageUrl }}
                style={styles.cover}
                resizeMode="cover"
                accessibilityIgnoresInvertColors
              />
            ) : null}

            <View style={styles.postHeader}>
              <View style={styles.metaRow}>
                {dateLabel ? (
                  <Text variant="caption" color={colors.text.tertiary}>
                    {t('blog.publishedOn')} {dateLabel}
                  </Text>
                ) : null}
              </View>

              <Text variant="h1" style={styles.postTitle} accessibilityRole="header">
                {post.title}
              </Text>

              {post.categories.length > 0 ? (
                <View style={styles.tagsRow}>
                  {post.categories.map((category) => (
                    <CategoryBadge key={category.key} label={category.name} />
                  ))}
                </View>
              ) : null}
            </View>

            <View style={styles.divider} />

            <View testID="blog-detail-body">
              <Markdown style={markdownStyles}>{post.body ?? ''}</Markdown>
            </View>
          </>
        ) : null}
      </Container>

      <PremiumFooter />
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.background.primary,
  },
  content: {
    flexGrow: 1,
    paddingTop: Platform.select({ web: 60, default: 40 }),
    paddingBottom: spacing.xxl,
  },
  backButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    marginBottom: spacing.xl,
    alignSelf: 'flex-start',
  },
  stateContainer: {
    paddingVertical: spacing.xxl,
    alignItems: 'center',
  },
  cover: {
    width: '100%',
    aspectRatio: 16 / 9,
    borderRadius: 8,
    marginBottom: spacing.xl,
    backgroundColor: colors.background.secondary,
  },
  postHeader: {
    marginBottom: spacing.lg,
    gap: spacing.sm,
  },
  metaRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    marginBottom: spacing.sm,
  },
  postTitle: {
    fontSize: Platform.select({ web: 42, default: 28 }),
    fontWeight: '700',
    letterSpacing: -0.5,
    color: colors.text.primary,
    marginBottom: spacing.sm,
  },
  tagsRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.xs,
  },
  tag: {
    backgroundColor: colors.background.secondary,
    borderRadius: 4,
    paddingHorizontal: spacing.xs,
    paddingVertical: 2,
  },
  tagText: {
    fontWeight: '500',
  },
  divider: {
    height: 1,
    backgroundColor: colors.border.muted,
    marginBottom: spacing.xxl,
  },
});
